import { useEffect, useState } from "react";

/**
 * Reactive viewport-width gate.
 *
 * Returns true while `window.innerWidth < breakpoint`. Subscribes to the
 * matching media query, so it flips immediately on resize / orientation
 * change without a polling tick.
 *
 * Default breakpoint is 1024px — the point at which the fixed 240px sidebar
 * stops leaving enough room for a usable main content column. Components
 * that need a tighter "phone-only" check can pass a smaller value.
 */
export function useIsMobile(breakpoint = 1024): boolean {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window === "undefined" ? false : window.innerWidth < breakpoint,
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [breakpoint]);

  return isMobile;
}
